import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { logger } from '@/utils/logger'
import { apiService } from '@/services/api'

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false)
  const [authMessage, setAuthMessage] = useState('')

  const { loginWithToken, loginDemo } = useAuth()
  const navigate = useNavigate()

  // Handle return from backend OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const authenticated = params.get('authenticated')
    const provider = params.get('provider')
    
    if (authenticated === 'true' && provider === 'google') {
      setAuthMessage('Signing you in...')
      setIsLoading(true)
      const token = params.get('token') || ''
      const email = params.get('email') || undefined
      
      const authPromise = token.length > 0
        ? loginWithToken(token, email)
        : ((import.meta as any).env?.PROD ? Promise.reject(new Error('No token in prod')) : loginDemo())
      
      authPromise
        .then(() => {
          window.history.replaceState({}, document.title, window.location.pathname)
          navigate('/campaign-builder', { replace: true })
        })
        .catch((err) => {
          console.error(err)
          setAuthMessage('Login failed. Please try again.')
        })
        .finally(() => setIsLoading(false))
    }
  }, [loginWithToken, loginDemo, navigate])

  const handleGoogleAuth = async () => {
    try {
      setIsLoading(true)
      setAuthMessage('Connecting to Google...')
      
      const response = await apiService.getGoogleDriveAuthUrl()
      const authUrl: string | undefined = response?.data?.authUrl
      
      // Validate OAuth URL
      let validOAuth = false
      try {
        if (authUrl) {
          const u = new URL(authUrl)
          const clientId = u.searchParams.get('client_id')
          validOAuth = Boolean(clientId && clientId !== 'undefined' && clientId !== 'null')
        }
      } catch {}

      if (validOAuth && authUrl && authUrl.includes('accounts.google.com')) {
        window.location.href = authUrl
      } else {
        // Fallback to demo mode
        if ((import.meta as any).env?.PROD) {
          setAuthMessage('Google auth unavailable. Please contact admin.')
        } else {
          setAuthMessage('Google auth unavailable. Entering demo mode...')
          await loginDemo()
          navigate('/campaign-builder', { replace: true })
        }
      }
    } catch (error) {
      logger.error('Google auth failed', undefined, error as Error)
      if ((import.meta as any).env?.PROD) {
        setAuthMessage('Google auth failed. Please try again.')
      } else {
        setAuthMessage('Google auth failed. Entering demo mode...')
        try {
          await loginDemo()
          navigate('/campaign-builder', { replace: true })
        } catch {}
      }
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50 dark:from-[#1B1B1F] dark:via-purple-900 dark:to-slate-900">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#9605DF] via-purple-600 to-indigo-600 p-12 flex-col justify-between">
        <div>
          <div className="mb-8">
            <img src="/dataraiils-logo-DR.png" alt="DataRaiils" className="h-12 w-auto drop-shadow-lg" />
          </div>
          <h1 className="text-5xl font-bold text-white mb-4 drop-shadow-lg">DataRaiils</h1>
          <p className="text-2xl text-blue-100 mb-12 drop-shadow">
            The Automation Layer Creative Ops Never Got
          </p>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-2 h-2 bg-white rounded-full mt-2 shadow-lg"></div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Pharmaceutical Campaign Management</h3>
                <p className="text-blue-100">BRD-driven workflows for compliant creative operations</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-2 h-2 bg-white rounded-full mt-2 shadow-lg"></div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">AI-Powered Asset Processing</h3>
                <p className="text-blue-100">Automated compliance checking and intelligent trafficking</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-2 h-2 bg-white rounded-full mt-2 shadow-lg"></div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Compliance & Trafficking Automation</h3>
                <p className="text-blue-100">FDA-compliant workflows with real-time validation</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-sm text-blue-100 opacity-80">
          © 2025 DataRaiils. All rights reserved.
        </div>
      </div>

      {/* Right Side - Auth */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Welcome Back</h2>
            <p className="text-gray-600 dark:text-gray-300">Sign in with your Google account to continue</p>
          </div>

          {authMessage && (
            <div className={`mb-6 p-4 rounded-lg text-sm text-center ${
              authMessage.includes('Connecting') || authMessage.includes('Signing')
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
            }`}>
              {authMessage}
            </div>
          )}

          <Button 
            onClick={handleGoogleAuth}
            disabled={isLoading}
            data-testid="auth-button-google-login"
            className="w-full h-14 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-900 dark:text-white border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center space-x-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02]"
          >
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span>Continue with Google</span>
              </>
            )}
          </Button>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              By continuing, you agree to our{' '}
              <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

